import React, { useState } from 'react';
import { theme } from '../../styles/theme';
import { CardCategoryIcon } from '../ui/CardCategoryIcon';
import { HQScreen } from './HQScreen';
import { RunState } from '../../types/run';
import { createCard } from '../../data/cards';

interface StyleguideScreenProps {
  onBack: () => void;
}

/**
 * Styleguide — single-page catalog of every UI pattern used in the game.
 *
 * Reference page only. Whenever you introduce a novel visual pattern,
 * add a new <Section> to this file with a live example + the spirit of
 * the pattern labeled.
 *
 * Sections:
 *   - Typography scale
 *   - Color palette
 *   - Buttons
 *   - Card representations (all eight variants)
 *   - Card keyword badges
 *   - Status chips
 *   - Passive chips
 *   - Message overlay (agent / intel / general)
 *   - Modal dialog shell
 *   - Map hover info bar
 *   - Exposure meter
 *   - Turn panel pieces
 *   - Pile buttons
 *   - Miscellaneous badges
 */
type StyleguideTab = 'components' | 'hq';

export function StyleguideScreen({ onBack }: StyleguideScreenProps) {
  const [activeTab, setActiveTab] = useState<StyleguideTab>('components');

  // HQ Preview tab — full-screen interactive HQ
  if (activeTab === 'hq') {
    return (
      <div style={styles.container}>
        <div style={styles.inner}>
          <div style={styles.headerRow}>
            <button style={styles.backButton} onClick={onBack}>← MENU</button>
            <h1 style={styles.title}>STYLEGUIDE</h1>
            <div style={styles.headerSpacer} />
          </div>
          <TabBar activeTab={activeTab} onTabChange={setActiveTab} />
          <button
            onClick={() => setActiveTab('components')}
            style={{
              position: 'absolute', top: '16px', left: '16px', zIndex: 100,
              background: 'rgba(255,255,255,0.06)', border: `1px solid ${theme.colors.border}`,
              color: theme.colors.textSecondary, fontFamily: theme.fonts.mono,
              fontSize: '14px', fontWeight: 700, padding: '4px 10px',
              cursor: 'pointer', borderRadius: '3px',
            }}
          >✕</button>
          <HQPreview />
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.inner}>
        <div style={styles.headerRow}>
          <button style={styles.backButton} onClick={onBack}>← MENU</button>
          <h1 style={styles.title}>STYLEGUIDE</h1>
          <div style={styles.headerSpacer} />
        </div>

        <TabBar activeTab={activeTab} onTabChange={setActiveTab} />

        <div style={styles.intro}>
          A single-page catalog of every UI pattern used in the game. Use it
          to audit visual consistency and take revision notes without having
          to play into each state. Every card variant and representation is
          showcased so you can compare them side-by-side.
        </div>

        {/* ── Typography ── */}
        <Section label="Typography">
          <SubsectionLabel>Display title</SubsectionLabel>
          <div style={{
            fontSize: '40px',
            letterSpacing: '10px',
            fontWeight: 700,
            color: theme.colors.textPrimary,
            fontFamily: theme.fonts.mono,
          }}>GEO-SLEEPER</div>

          <SubsectionLabel>Screen title</SubsectionLabel>
          <div style={{
            fontSize: '24px',
            letterSpacing: '6px',
            fontWeight: 700,
            color: theme.colors.textPrimary,
            fontFamily: theme.fonts.mono,
          }}>CARDS</div>

          <SubsectionLabel>Section header</SubsectionLabel>
          <div style={{
            fontSize: '11px',
            letterSpacing: '3px',
            fontWeight: 700,
            color: theme.colors.accentBlue,
            fontFamily: theme.fonts.mono,
          }}>▍ INTEL · 7</div>

          <SubsectionLabel>Body text</SubsectionLabel>
          <div style={{
            fontSize: '11px',
            lineHeight: 1.6,
            color: theme.colors.textSecondary,
            fontFamily: theme.fonts.mono,
            maxWidth: '540px',
          }}>
            Plays a surveillance sweep anywhere on the map. Turns red if the
            agent is inside the zone. Cannot be fooled by decoys.
          </div>

          <SubsectionLabel>Micro labels / captions</SubsectionLabel>
          <div style={{
            fontSize: '8px',
            letterSpacing: '2px',
            color: theme.colors.textDim,
            fontWeight: 700,
            fontFamily: theme.fonts.mono,
          }}>DURATION · 3 TURNS LEFT</div>
        </Section>

        {/* ── Color palette ── */}
        <Section label="Color palette">
          <SubsectionLabel>Base surfaces</SubsectionLabel>
          <Swatches items={[
            ['background', theme.colors.background],
            ['surface', theme.colors.surface],
            ['surfaceLight', theme.colors.surfaceLight],
            ['border', theme.colors.border],
          ]} />

          <SubsectionLabel>Text</SubsectionLabel>
          <Swatches items={[
            ['textPrimary', theme.colors.textPrimary],
            ['textSecondary', theme.colors.textSecondary],
            ['textDim', theme.colors.textDim],
            ['textAccent', theme.colors.textAccent],
          ]} />

          <SubsectionLabel>UI accents</SubsectionLabel>
          <Swatches items={[
            ['accentGreen', theme.colors.accentGreen],
            ['accentBlue', theme.colors.accentBlue],
            ['accentYellow', theme.colors.accentYellow],
            ['accentOrange', theme.colors.accentOrange],
            ['accentRed', theme.colors.accentRed],
          ]} />

          <SubsectionLabel>Game elements</SubsectionLabel>
          <Swatches items={[
            ['playerMarker', theme.colors.playerMarker],
            ['agentMarker', theme.colors.agentMarker],
            ['signalDecoy', theme.colors.signalDecoy],
            ['itemPickup', theme.colors.itemPickup],
          ]} />

          <SubsectionLabel>Card category colors</SubsectionLabel>
          <Swatches items={[
            ['movement', '#66ccff'],
            ['intel', '#aa88ff'],
            ['tactics', '#ffaa44'],
            ['combat', '#ff5555'],
            ['fieldcraft', '#44cc88'],
          ]} />

          <SubsectionLabel>Passive bucket colors</SubsectionLabel>
          <Swatches items={[
            ['training', '#66ccff'],
            ['background', '#aa88ff'],
            ['contacts', '#ffaa44'],
            ['loadout', '#44cc88'],
            ['perception', '#ff5ac4'],
            ['economy', '#ffcc44'],
            ['combat', '#ff5555'],
          ]} />
        </Section>

        {/* ── Buttons ── */}
        <Section label="Buttons">
          <SubsectionLabel>Primary run (menu)</SubsectionLabel>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button style={{
              background: 'rgba(68, 204, 136, 0.06)',
              border: '1px solid rgba(68, 204, 136, 0.5)',
              color: '#44cc88',
              padding: '16px 24px',
              fontSize: '15px',
              fontWeight: 700,
              letterSpacing: '4px',
              fontFamily: theme.fonts.mono,
              borderRadius: '4px',
              cursor: 'pointer',
            }}>NEW RUN</button>

            <button style={{
              background: 'rgba(68, 170, 255, 0.06)',
              border: '1px solid rgba(68, 170, 255, 0.5)',
              color: '#44aaff',
              padding: '14px 24px',
              fontSize: '14px',
              fontWeight: 700,
              letterSpacing: '3px',
              fontFamily: theme.fonts.mono,
              borderRadius: '4px',
              cursor: 'pointer',
            }}>CONTINUE RUN</button>
          </div>

          <SubsectionLabel>Secondary / subtle</SubsectionLabel>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button style={{
              background: 'none',
              border: `1px solid ${theme.colors.border}`,
              color: theme.colors.textSecondary,
              padding: '10px 24px',
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '3px',
              fontFamily: theme.fonts.mono,
              borderRadius: '4px',
              cursor: 'pointer',
            }}>PRACTICE</button>

            <button style={{
              background: 'rgba(255, 255, 255, 0.03)',
              border: `1px solid ${theme.colors.border}`,
              color: theme.colors.textSecondary,
              padding: '8px 14px',
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '2px',
              fontFamily: theme.fonts.mono,
              borderRadius: '4px',
              cursor: 'pointer',
            }}>← MENU</button>
          </div>

          <SubsectionLabel>In-game actions</SubsectionLabel>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button style={{
              background: 'rgba(255, 80, 80, 0.1)',
              border: '1px solid rgba(255, 80, 80, 0.4)',
              borderRadius: '8px',
              padding: '12px 16px',
              color: '#ff5050',
              fontSize: '13px',
              fontWeight: 700,
              fontFamily: theme.fonts.mono,
              cursor: 'pointer',
            }}>ATTACK</button>

            <button style={{
              background: 'rgba(30, 38, 50, 0.8)',
              border: `1px solid ${theme.colors.border}`,
              borderRadius: '8px',
              padding: '10px 16px',
              color: theme.colors.textSecondary,
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '2px',
              fontFamily: theme.fonts.mono,
              cursor: 'pointer',
            }}>BACK OFF</button>
          </div>
        </Section>

        {/* ── Card representations ── */}
        <Section label="Card representations">
          <SubsectionLabel>
            The game renders cards in eight different contexts. They are
            visually distinct — audit this section when changing card UI.
          </SubsectionLabel>

          <CardVariantBlock
            label="1. Hand card (.hand-card)"
            note="GameScreen hand, bottom of the screen during a run. Interactive — hover lifts, shows tooltip. 90×120."
          >
            <HandCardReal name="Sweep" category="intel" apCost={2} />
          </CardVariantBlock>

          <CardVariantBlock
            label="2. Scavenge / virtual card (.hand-card.hand-card--scavenge)"
            note="Yellow-glow variant. Used for virtual cards injected into hand: scavenge (item pickup), extract, collect passphrase, enter safehouse. Not a real card in the deck."
          >
            <div className="hand-card hand-card--scavenge" style={{ cursor: 'default' }}>
              <div style={{ position: 'absolute', top: '6px', left: '8px', fontSize: '14px', fontWeight: 700, color: theme.colors.accentYellow, fontFamily: theme.fonts.mono }}>0</div>
              <div style={{ fontSize: '11px', fontWeight: 600, color: theme.colors.textPrimary, fontFamily: theme.fonts.mono }}>Scavenge</div>
              <div style={{ fontSize: '8px', letterSpacing: '1.5px', color: theme.colors.textDim, fontFamily: theme.fonts.mono }}>ITEM</div>
            </div>
          </CardVariantBlock>

          <CardVariantBlock
            label="3. Deck draft card (DeckDraftScreen)"
            note="Initial deck-building screen at run start. 100×135 full, 70×95 mini. Categorical color accent. Colored AP pill top-left."
          >
            <DraftCardMini name="Hide Inside" category="tactics" apCost={1} />
          </CardVariantBlock>

          <CardVariantBlock
            label="4. Card drop card (CardDropScreen)"
            note="Shown at round end when forced to ship a card to HQ. Wide text card, 190px, vertical layout. Category-colored top label."
          >
            <CardDropCardMini
              name="Counter-Surveillance"
              category="intel"
              apCost={1}
              description="See the agent's last signal on you. Reveals your own signal footprint. Reduces exposure by 100."
            />
          </CardVariantBlock>

          <CardVariantBlock
            label="5. HQ shop draft card (HQScreen)"
            note="Shown when drafting a new card at HQ. Wide list-style, full description visible. Category dot."
          >
            <HQDraftCardMini
              name="Acoustic Ping"
              category="intel"
              apCost={2}
              description="Reveals the distance to the agent as a ring around your position. Use multiple pings from different spots to triangulate."
            />
          </CardVariantBlock>

          <CardVariantBlock
            label="6. HQ deck row (HQScreen)"
            note="Compact list item for browsing the full deck at HQ. Dot + name + cost. Used for high-density overviews."
          >
            <HQDeckRowMini name="Move" category="movement" apCost={1} />
            <HQDeckRowMini name="Sweep" category="intel" apCost={2} />
            <HQDeckRowMini name="Improvised Strike" category="combat" apCost={0} />
          </CardVariantBlock>

          <CardVariantBlock
            label="7. Card picker modal card (CardPicker)"
            note="Used by fieldcraft effects that require the player to pick a card from hand/deck/discard (Adapt, Copy, Recall, Stash, Field Modify, Overclock, Improvise). 150px grid. Yellow AP pill top-left, icon top-right."
          >
            <CardPickerCardMini
              name="Move"
              category="movement"
              apCost={1}
              description="Move up to 300m along the street network."
              range={300}
            />
          </CardVariantBlock>

          <CardVariantBlock
            label="8. Pile overlay card (deck / discard view)"
            note="Shown when clicking the DRAW or DISCARD pile. Wide list-style, full details. Used for browsing all cards in a pile."
          >
            <PileOverlayCardMini
              name="Rooftop Access"
              category="tactics"
              apCost={3}
              description="Climb to a rooftop of an adjacent commercial building within 50m."
            />
          </CardVariantBlock>

          <SubsectionLabel>Card tooltip (hover on hand card)</SubsectionLabel>
          <CardTooltipExample />
        </Section>

        {/* ── Card keyword badges ── */}
        <Section label="Card keyword badges">
          <SubsectionLabel>Inline badges that appear on card visuals</SubsectionLabel>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            <KeywordBadge label="BURN" bg="rgba(255, 80, 50, 0.25)" border="#ff503250" color="#ff5032" />
            <KeywordBadge label="CHAIN" bg="rgba(68, 170, 255, 0.25)" border="#44aaff50" color="#44aaff" />
            <KeywordBadge label="STASHED" bg="rgba(68, 204, 136, 0.25)" border="#44cc8850" color="#44cc88" />
          </div>
        </Section>

        {/* ── Status chips ── */}
        <Section label="Status chips">
          <SubsectionLabel>Single-row indicators above the hand. Each effect is one chip.</SubsectionLabel>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            <StatusChipMini label="HIDE INSIDE" color="#44cc88" />
            <StatusChipMini label="ROOFTOP" color="#00e5ff" />
            <StatusChipMini label="INSTINCT" color="#ffaa44" />
            <StatusChipMini label="DETECTED" color="#ff4040" />
            <StatusChipMini label="ENEMY SEEN" color="#ff8844" />
            <StatusChipMini label="INFORMANT" color="#aa88ff" />
            <StatusChipMini label="DISTRICT SCAN" color="#aa88ff" />
            <StatusChipMini label="PING 340m" color="#66ccff" />
            <StatusChipMini label="WIRE TAP ×2" color="#ffdd44" />
            <StatusChipMini label="PREP +20%" color="#ff5555" />
            <StatusChipMini label="STASHED" color="#aa88ff" />
            <StatusChipMini label="+1 AP NEXT" color="#ffcc44" />
            <StatusChipMini label="ADRENALINE +1 AP" color="#ff8844" />
            <StatusChipMini label="MOVE BUFF" color="#44cc88" />
            <StatusChipMini label="SCANNER" color="#44aaff" />
            <StatusChipMini label="SAFEHOUSE" color="#44cc88" />
            <StatusChipMini label="GHOST" color="#44cc88" />
          </div>
        </Section>

        {/* ── Passive chips ── */}
        <Section label="Passive chips (HUD traits row)">
          <SubsectionLabel>Shown at top of map during runs. Bucket-colored border + text. Tooltip on hover.</SubsectionLabel>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            <PassiveChipMini label="Tactician" color="#66ccff" />
            <PassiveChipMini label="Deep Cover" color="#aa88ff" />
            <PassiveChipMini label="Quartermaster" color="#ffaa44" />
            <PassiveChipMini label="Concealed Carry" color="#44cc88" />
            <PassiveChipMini label="Cold Opening" color="#ff5ac4" />
            <PassiveChipMini label="Bag Man" color="#ffcc44" />
            <PassiveChipMini label="Hard Target" color="#ff5555" />
          </div>
        </Section>

        {/* ── Message overlay ── */}
        <Section label="Message overlay">
          <SubsectionLabel>Temporary messages above the hand. Three variant colors.</SubsectionLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start' }}>
            <MessageOverlayMini variant="general" text="Wire Tap placed. Waiting for agent to cross..." />
            <MessageOverlayMini variant="intel" text="Informant: agent spotted near Eastside Market on 3rd Ave." />
            <MessageOverlayMini variant="agent" text="Eyes locked. The agent sees you too." />
            <MessageOverlayMini variant="general" kbd="SHIFT" text="Hold to use footpaths" />
          </div>
        </Section>

        {/* ── Modal dialog shell ── */}
        <Section label="Modal dialog shell">
          <SubsectionLabel>Combat, safehouse draft, round-end all use this pattern.</SubsectionLabel>
          <div style={{
            background: theme.colors.surface,
            border: '2px solid #ff5050',
            borderRadius: '12px',
            width: '360px',
            fontFamily: theme.fonts.mono,
            overflow: 'hidden',
          }}>
            <div style={{
              padding: '14px 18px',
              background: 'rgba(255, 80, 80, 0.08)',
              borderBottom: `1px solid ${theme.colors.border}`,
            }}>
              <div style={{
                fontSize: '10px',
                letterSpacing: '3px',
                color: theme.colors.textDim,
                marginBottom: '4px',
              }}>CONFRONTATION</div>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#ff5050' }}>
                Both aware. Choose your move.
              </div>
            </div>
            <div style={{ padding: '16px 18px', fontSize: '11px', color: theme.colors.textSecondary, lineHeight: 1.6 }}>
              Modal body content sits here — weapon choices, results, prep
              stats, roll log, etc. Header color matches the outcome state.
            </div>
          </div>
        </Section>

        {/* ── Map hover info bar ── */}
        <Section label="Map hover info bar">
          <SubsectionLabel>Shown at top of map when hovering streets/zones.</SubsectionLabel>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '12px',
            padding: '5px 14px',
            background: 'rgba(10, 14, 20, 0.92)',
            border: `1px solid ${theme.colors.border}`,
            borderRadius: '4px',
            fontFamily: theme.fonts.mono,
          }}>
            <span style={{ fontSize: '9px', color: theme.colors.textDim, letterSpacing: '2px', fontWeight: 700 }}>ROAD</span>
            <span style={{ fontSize: '12px', color: theme.colors.textPrimary, fontWeight: 600 }}>5th Avenue</span>
            <span style={{
              display: 'flex',
              gap: '6px',
              alignItems: 'center',
              padding: '2px 8px',
              borderRadius: '3px',
              background: 'rgba(248, 113, 113, 0.08)',
              border: '1px solid rgba(248, 113, 113, 0.3)',
            }}>
              <span style={{ fontSize: '8px', color: '#f87171', fontWeight: 700, letterSpacing: '1px' }}>MAJOR</span>
              <span style={{ fontSize: '9px', color: '#f87171' }}>⚡1.0</span>
            </span>
            <span style={{
              display: 'flex',
              gap: '6px',
              alignItems: 'center',
              padding: '2px 8px',
              borderRadius: '3px',
              background: 'rgba(170, 136, 255, 0.08)',
              border: '1px solid rgba(170, 136, 255, 0.3)',
            }}>
              <span style={{ fontSize: '8px', color: '#aa88ff', fontWeight: 700, letterSpacing: '1px' }}>COMMERCIAL</span>
              <span style={{ fontSize: '9px', color: '#aa88ff' }}>×2.0</span>
            </span>
          </div>
        </Section>

        {/* ── Exposure meter ── */}
        <Section label="Exposure meter">
          <SubsectionLabel>Shown next to the turn panel. Bar with threshold markers and a label.</SubsectionLabel>
          <div style={{ width: '220px', fontFamily: theme.fonts.mono }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '3px' }}>
              <span style={{ fontSize: '9px', letterSpacing: '1.5px', color: '#ffaa30', fontWeight: 700 }}>EXPOSURE</span>
              <span style={{ fontSize: '8px', letterSpacing: '0.5px', color: '#ffaa30', fontWeight: 600 }}>PARTIAL</span>
            </div>
            <div style={{
              width: '100%',
              height: '8px',
              background: 'rgba(255, 255, 255, 0.06)',
              borderRadius: '4px',
              overflow: 'hidden',
              position: 'relative',
            }}>
              <div style={{ position: 'absolute', left: '20%', top: 0, width: '1px', height: '100%', background: 'rgba(255, 255, 255, 0.25)' }} />
              <div style={{ position: 'absolute', left: '50%', top: 0, width: '1px', height: '100%', background: 'rgba(255, 255, 255, 0.25)' }} />
              <div style={{ width: '35%', height: '100%', background: '#ffaa30', borderRadius: '4px' }} />
            </div>
          </div>
        </Section>

        {/* ── Turn panel pieces ── */}
        <Section label="Turn panel pieces">
          <SubsectionLabel>Right side of the hand area — AP, turn, end-turn.</SubsectionLabel>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{
              fontSize: '13px',
              letterSpacing: '1.5px',
              color: theme.colors.textSecondary,
              fontWeight: 500,
              background: 'rgba(12, 16, 24, 0.95)',
              border: `1px solid ${theme.colors.border}`,
              borderRadius: '4px',
              padding: '6px 14px',
              fontFamily: theme.fonts.mono,
            }}>
              AP <span style={{ color: theme.colors.accentYellow, fontWeight: 700 }}>3/3</span>
            </div>
            <div style={{
              fontSize: '13px',
              letterSpacing: '2px',
              color: theme.colors.textSecondary,
              fontWeight: 500,
              background: 'rgba(12, 16, 24, 0.95)',
              border: `1px solid ${theme.colors.border}`,
              borderRadius: '4px',
              padding: '6px 14px',
              fontFamily: theme.fonts.mono,
            }}>TURN 7</div>
            <div style={{
              fontSize: '11px',
              letterSpacing: '1px',
              color: theme.colors.textSecondary,
              fontWeight: 500,
              background: 'rgba(12, 16, 24, 0.95)',
              border: `1px solid ${theme.colors.border}`,
              borderRadius: '4px',
              padding: '4px 10px',
              fontFamily: theme.fonts.mono,
            }}>$240 <span style={{ color: theme.colors.textDim, fontSize: '9px' }}>MARKS</span></div>
            <div className="end-turn-button" style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px',
              padding: '12px 24px',
              background: 'rgba(12, 16, 24, 0.95)',
              border: `1px solid ${theme.colors.border}`,
              borderRadius: '6px',
              color: theme.colors.textPrimary,
              fontFamily: theme.fonts.mono,
              fontSize: '13px',
              fontWeight: 600,
              letterSpacing: '2px',
              cursor: 'pointer',
            }}>
              <span>END TURN</span>
              <span style={{ fontSize: '18px', color: theme.colors.textSecondary }}>×</span>
            </div>
          </div>
        </Section>

        {/* ── Pile buttons ── */}
        <Section label="Pile buttons (deck / discard)">
          <SubsectionLabel>Flanking the hand. Show card count + label. Click to expand.</SubsectionLabel>
          <div style={{ display: 'flex', gap: '8px' }}>
            <div style={styles.pile}>
              <div style={styles.pileCount}>18</div>
              <div style={styles.pileLabel}>DECK</div>
            </div>
            <div style={styles.pile}>
              <div style={styles.pileCount}>3</div>
              <div style={styles.pileLabel}>DISCARD</div>
            </div>
          </div>
        </Section>

        {/* ── Meta badges ── */}
        <Section label="Meta badges">
          <SubsectionLabel>Small labels used on modal headers, caption rows, hover tooltips.</SubsectionLabel>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            <MetaBadge label="MOVEMENT" color="#66ccff" />
            <MetaBadge label="INTEL" color="#aa88ff" />
            <MetaBadge label="TACTICS" color="#ffaa44" />
            <MetaBadge label="COMBAT" color="#ff5555" />
            <MetaBadge label="FIELDCRAFT" color="#44cc88" />
            <MetaBadge label="STARTER" color="#44cc88" />
            <MetaBadge label="BUFF" color="#44cc88" />
          </div>
        </Section>

        {/* ── Footer ── */}
        <div style={styles.footer}>
          Reference page only — not a live game state. When you introduce a
          new visual pattern, add a <code style={styles.codePath}>&lt;Section&gt;</code> to
          <code style={styles.codePath}>src/components/screens/StyleguideScreen.tsx</code>.
        </div>
      </div>
    </div>
  );
}

// --- Tab bar ---

function TabBar({ activeTab, onTabChange }: { activeTab: StyleguideTab; onTabChange: (t: StyleguideTab) => void }) {
  const tabs: { key: StyleguideTab; label: string }[] = [
    { key: 'components', label: 'COMPONENTS' },
    { key: 'hq', label: 'HQ PREVIEW' },
  ];
  return (
    <div style={{ display: 'flex', gap: '4px', marginBottom: '24px' }}>
      {tabs.map(t => (
        <button
          key={t.key}
          onClick={() => onTabChange(t.key)}
          style={{
            padding: '8px 16px', fontSize: '10px', fontWeight: 700, letterSpacing: '2px',
            fontFamily: theme.fonts.mono, borderRadius: '3px', cursor: 'pointer',
            border: `1px solid ${t.key === activeTab ? theme.colors.accentGreen + '60' : theme.colors.border}`,
            background: t.key === activeTab ? 'rgba(0, 255, 136, 0.08)' : 'none',
            color: t.key === activeTab ? theme.colors.accentGreen : theme.colors.textDim,
            transition: 'all 0.15s',
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// --- HQ Preview (mock data, fully interactive) ---

function createMockRunState(): RunState {
  return {
    runId: 'styleguide-mock',
    round: 3,
    cityHistory: ['Dublin', 'Prague'],
    deck: [
      createCard('move'), createCard('move'), createCard('move'),
      createCard('move'), createCard('shortcut'),
      createCard('sweep'), createCard('informant'),
      createCard('hideInside'), createCard('disguise'),
      createCard('improvisedStrike'), createCard('instinct'),
      createCard('quickStep'),
    ],
    droppedCards: [
      { card: createCard('spotter'), roundDropped: 2 },
    ],
    marks: 450,
    unbankedMarks: 125,
    collectedItems: [
      { id: 'pistol-mock', name: 'Pistol', category: 'weapon', apCost: 1, description: 'Standard sidearm.', sellValue: 40 },
      { id: 'jammer-mock', name: 'Jammer', category: 'gadget', apCost: 0, description: 'Signal suppression.', sellValue: 30 },
    ],
    safehouseCardsThisRound: [],
    passives: ['concealedCarry'],
    lastRoundResult: 'escape',
    shopDraftCount: 0,
    shopPackCounts: { intel: 0, tactics: 0, fieldcraft: 0 },
    directBuyUsed: false,
    savedAt: Date.now(),
  };
}

function HQPreview() {
  const [key, setKey] = useState(0);

  return (
    <div>
      <div style={{ fontSize: '10px', color: theme.colors.textSecondary, marginBottom: '12px', lineHeight: '1.6' }}>
        Fully interactive HQ screen with mock data. Deploy and End Run are intercepted
        (they just reset the preview). Use this to test the shop flow without playing a round.
      </div>
      <button
        style={{
          background: 'rgba(255,255,255,0.04)', border: `1px solid ${theme.colors.border}`,
          color: theme.colors.textSecondary, padding: '6px 14px', fontSize: '9px',
          fontWeight: 600, letterSpacing: '2px', cursor: 'pointer',
          fontFamily: theme.fonts.mono, borderRadius: '3px', marginBottom: '16px',
        }}
        onClick={() => setKey(k => k + 1)}
      >
        RESET PREVIEW
      </button>
      <div style={{
        position: 'relative', width: '100%', height: '700px',
        border: `1px solid ${theme.colors.border}`, borderRadius: '6px', overflow: 'hidden',
      }}>
        <div key={key} style={{ position: 'absolute', inset: 0 }}>
          <HQScreen
            runState={createMockRunState()}
            onDeploy={() => setKey(k => k + 1)}
            onEndRun={() => setKey(k => k + 1)}
            shopEnabled={true}
          />
        </div>
      </div>
    </div>
  );
}

// --- Section wrappers ---

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section style={styles.section}>
      <div style={styles.sectionLabel}>
        <span style={styles.sectionMark}>▍</span>
        {label.toUpperCase()}
      </div>
      <div style={styles.sectionBody}>{children}</div>
    </section>
  );
}

function SubsectionLabel({ children }: { children: React.ReactNode }) {
  return <div style={styles.subsectionLabel}>{children}</div>;
}

// --- Card variant block: label + note above, live example below ---

function CardVariantBlock({
  label,
  note,
  children,
}: {
  label: string;
  note: string;
  children: React.ReactNode;
}) {
  return (
    <div style={styles.cardVariantBlock}>
      <div style={styles.cardVariantLabel}>{label}</div>
      <div style={styles.cardVariantNote}>{note}</div>
      <div style={styles.cardVariantShowcase}>{children}</div>
    </div>
  );
}

// --- Swatch grid for color palette ---

function Swatches({ items }: { items: Array<[string, string]> }) {
  return (
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
      {items.map(([name, color]) => (
        <div key={name} style={styles.swatch}>
          <div style={{ ...styles.swatchBlock, background: color }} />
          <div style={styles.swatchName}>{name}</div>
          <div style={styles.swatchHex}>{color}</div>
        </div>
      ))}
    </div>
  );
}

// --- Card variants ---

type CategoryKey = 'movement' | 'intel' | 'tactics' | 'combat' | 'fieldcraft';

const CATEGORY_COLORS: Record<CategoryKey, string> = {
  movement: '#66ccff',
  intel: '#aa88ff',
  tactics: '#ffaa44',
  combat: '#ff5555',
  fieldcraft: '#44cc88',
};

// 1. Hand card — uses the REAL .hand-card class from global.css
function HandCardReal({ name, category, apCost }: { name: string; category: CategoryKey; apCost: number }) {
  return (
    <div className="hand-card" style={{ cursor: 'default' }}>
      <div style={{ position: 'absolute', top: '6px', left: '8px', fontSize: '14px', fontWeight: 700, color: theme.colors.accentYellow, fontFamily: theme.fonts.mono }}>{apCost}</div>
      <div style={{ fontSize: '11px', fontWeight: 600, color: theme.colors.textPrimary, letterSpacing: '0.5px', textAlign: 'center', padding: '0 6px', fontFamily: theme.fonts.mono }}>{name}</div>
      <div style={{ fontSize: '8px', letterSpacing: '1.5px', color: theme.colors.textDim, fontFamily: theme.fonts.mono }}>{category.toUpperCase()}</div>
      <div style={{ position: 'absolute', bottom: '4px', left: '4px', display: 'flex', alignItems: 'center', opacity: 0.9 }}>
        <CardCategoryIcon category={category} size={16} />
      </div>
    </div>
  );
}

// 3. Deck draft card — mirrors DeckDraftScreen.renderCard
function DraftCardMini({ name, category, apCost }: { name: string; category: CategoryKey; apCost: number }) {
  const catColor = CATEGORY_COLORS[category];
  return (
    <div style={{
      width: '100px',
      height: '135px',
      background: 'rgba(12, 16, 24, 0.95)',
      border: '1.5px solid rgba(255,255,255,0.12)',
      borderRadius: '6px',
      padding: '8px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '4px',
      position: 'relative',
      fontFamily: theme.fonts.mono,
    }}>
      <div style={{
        position: 'absolute',
        top: '4px',
        left: '5px',
        width: '22px',
        height: '22px',
        borderRadius: '4px',
        background: catColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        fontWeight: 800,
        color: '#000',
      }}>{apCost}</div>
      <div style={{
        fontSize: '11px',
        fontWeight: 700,
        color: theme.colors.textPrimary,
        textAlign: 'center',
        lineHeight: 1.2,
        marginTop: '18px',
      }}>{name}</div>
      <div style={{
        fontSize: '8px',
        color: catColor,
        letterSpacing: '1px',
        textTransform: 'uppercase',
      }}>{category}</div>
    </div>
  );
}

// 4. Card drop card — mirrors CardDropScreen.cardButton
function CardDropCardMini({
  name, category, apCost, description,
}: { name: string; category: CategoryKey; apCost: number; description: string }) {
  const catColor = CATEGORY_COLORS[category];
  return (
    <div style={{
      background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '6px',
      padding: '20px 16px',
      width: '190px',
      fontFamily: theme.fonts.mono,
      textAlign: 'left',
      position: 'relative',
    }}>
      <div style={{ fontSize: '9px', letterSpacing: '2px', fontWeight: 700, color: catColor, marginBottom: '8px' }}>
        {category.toUpperCase()}
      </div>
      <div style={{ fontSize: '14px', fontWeight: 700, color: theme.colors.textPrimary, marginBottom: '4px', letterSpacing: '0.5px' }}>
        {name}
      </div>
      <div style={{ fontSize: '10px', color: theme.colors.textSecondary, marginBottom: '10px', letterSpacing: '1px' }}>
        {apCost} AP
      </div>
      <div style={{ fontSize: '10px', color: theme.colors.textSecondary, lineHeight: 1.5 }}>
        {description}
      </div>
    </div>
  );
}

// 5. HQ shop draft card
function HQDraftCardMini({
  name, category, apCost, description,
}: { name: string; category: CategoryKey; apCost: number; description: string }) {
  const catColor = CATEGORY_COLORS[category];
  return (
    <div style={{
      background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '4px',
      padding: '12px',
      fontFamily: theme.fonts.mono,
      textAlign: 'left',
      width: '280px',
    }}>
      <div style={{ fontSize: '8px', letterSpacing: '2px', fontWeight: 700, color: catColor, marginBottom: '4px' }}>
        {category.toUpperCase()}
      </div>
      <div style={{ fontSize: '12px', fontWeight: 700, color: theme.colors.textPrimary, marginBottom: '2px' }}>
        {name}
      </div>
      <div style={{ fontSize: '9px', color: theme.colors.textSecondary, marginBottom: '6px' }}>
        {apCost} AP
      </div>
      <div style={{ fontSize: '9px', color: theme.colors.textSecondary, lineHeight: 1.4 }}>
        {description}
      </div>
    </div>
  );
}

// 6. HQ deck row
function HQDeckRowMini({ name, category, apCost }: { name: string; category: CategoryKey; apCost: number }) {
  const catColor = CATEGORY_COLORS[category];
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '4px 0',
      fontFamily: theme.fonts.mono,
    }}>
      <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: catColor, flexShrink: 0 }} />
      <span style={{ fontSize: '11px', color: theme.colors.textPrimary, flex: 1 }}>{name}</span>
      <span style={{ fontSize: '10px', color: theme.colors.textDim }}>{apCost} AP</span>
    </div>
  );
}

// 7. CardPicker card (fieldcraft effect modals)
function CardPickerCardMini({
  name, category, apCost, description, range,
}: { name: string; category: CategoryKey; apCost: number; description: string; range?: number }) {
  const catColor = CATEGORY_COLORS[category];
  return (
    <div style={{
      background: theme.colors.surface,
      border: `1px solid ${catColor}30`,
      borderRadius: '6px',
      padding: '12px',
      position: 'relative',
      fontFamily: theme.fonts.mono,
      width: '150px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <div style={{
          background: '#ffcc00',
          color: '#000',
          fontWeight: 700,
          fontSize: '11px',
          width: '20px',
          height: '20px',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>{apCost}</div>
        <div style={{ opacity: 0.7 }}>
          <CardCategoryIcon category={category} size={14} />
        </div>
      </div>
      <div style={{ fontSize: '12px', fontWeight: 700, color: theme.colors.textPrimary, marginBottom: '2px' }}>{name}</div>
      <div style={{ fontSize: '9px', letterSpacing: '1px', marginBottom: '6px', color: catColor }}>{category.toUpperCase()}</div>
      {range !== undefined && (
        <div style={{ fontSize: '10px', color: theme.colors.accentBlue, marginBottom: '4px' }}>{range}m</div>
      )}
      <div style={{ fontSize: '9px', color: theme.colors.textSecondary, lineHeight: 1.4 }}>{description}</div>
    </div>
  );
}

// 8. Pile overlay card (deck/discard expanded view)
function PileOverlayCardMini({
  name, category, apCost, description,
}: { name: string; category: CategoryKey; apCost: number; description: string }) {
  const catColor = CATEGORY_COLORS[category];
  return (
    <div style={{
      position: 'relative',
      padding: '10px 12px',
      background: 'rgba(12, 16, 24, 0.85)',
      border: `1px solid ${catColor}30`,
      borderRadius: '5px',
      width: '260px',
      fontFamily: theme.fonts.mono,
    }}>
      <div style={{
        position: 'absolute',
        top: '8px',
        left: '10px',
        fontSize: '11px',
        fontWeight: 700,
        color: '#000',
        background: '#ffcc00',
        padding: '1px 5px',
        borderRadius: '3px',
      }}>{apCost}</div>
      <div style={{
        fontSize: '12px',
        fontWeight: 700,
        color: theme.colors.textPrimary,
        paddingLeft: '34px',
        paddingRight: '28px',
        minHeight: '20px',
      }}>
        {name}
        <span style={{ position: 'absolute', top: '10px', right: '10px', opacity: 0.7 }}>
          <CardCategoryIcon category={category} size={14} />
        </span>
      </div>
      <div style={{ fontSize: '9px', letterSpacing: '1.5px', fontWeight: 700, color: catColor, marginTop: '4px', marginBottom: '6px' }}>
        {category.toUpperCase()}
      </div>
      <div style={{ fontSize: '10px', color: theme.colors.textSecondary, lineHeight: 1.4 }}>{description}</div>
    </div>
  );
}

// --- Card tooltip standalone example ---
// The real .card-tooltip only renders on hand-card:hover, so we inline the
// exact visual here with `display: block` so the styleguide shows it.
function CardTooltipExample() {
  return (
    <div style={{
      position: 'relative',
      display: 'block',
      width: '200px',
      background: 'rgba(10, 14, 20, 0.96)',
      border: '1px solid #2a3a4f',
      borderRadius: '6px',
      padding: '10px 12px',
      fontFamily: theme.fonts.mono,
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.6)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
        <span style={{ fontSize: '13px', fontWeight: 600, color: '#c8d6e5', letterSpacing: '0.5px' }}>Sweep</span>
        <span style={{ fontSize: '11px', fontWeight: 700, color: '#ffdd44', letterSpacing: '0.5px' }}>2 AP</span>
      </div>
      <div style={{ fontSize: '8px', letterSpacing: '2px', color: '#3d4f63', marginBottom: '8px', paddingBottom: '6px', borderBottom: '1px solid #1e2a3a' }}>
        INTEL
      </div>
      <div style={{ fontSize: '11px', lineHeight: 1.5, color: '#9ab0c8', marginBottom: '8px' }}>
        Place a 500m scan zone anywhere on the map. Turns red if the agent is inside. Only detects real contacts — ignores decoys.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <div style={{ fontSize: '10px', letterSpacing: '0.5px', color: '#44aaff', fontWeight: 500 }}>Scan Radius: 0.5km</div>
      </div>
    </div>
  );
}

// --- Small components reused across sections ---

function StatusChipMini({ label, color }: { label: string; color: string }) {
  return (
    <div style={{
      fontSize: '9px',
      fontFamily: theme.fonts.mono,
      fontWeight: 700,
      letterSpacing: '1px',
      padding: '4px 8px',
      borderRadius: '3px',
      border: `1px solid ${color}60`,
      background: `${color}14`,
      color,
    }}>{label}</div>
  );
}

function PassiveChipMini({ label, color }: { label: string; color: string }) {
  return (
    <div style={{
      fontSize: '9px',
      fontFamily: theme.fonts.mono,
      fontWeight: 600,
      letterSpacing: '0.5px',
      padding: '3px 7px',
      borderRadius: '3px',
      border: `1px solid ${color}60`,
      background: 'rgba(255, 255, 255, 0.02)',
      color,
    }}>{label}</div>
  );
}

function MessageOverlayMini({
  variant, text, kbd,
}: { variant: 'agent' | 'intel' | 'general'; text: string; kbd?: string }) {
  const variantStyle = variant === 'agent' ? {
    background: 'rgba(40, 10, 12, 0.92)',
    borderColor: 'rgba(255, 80, 80, 0.55)',
    color: '#ff9a9a',
  } : variant === 'intel' ? {
    background: 'rgba(26, 16, 48, 0.92)',
    borderColor: 'rgba(170, 136, 255, 0.55)',
    color: '#c9b8ff',
  } : {
    background: 'rgba(12, 20, 36, 0.92)',
    borderColor: 'rgba(68, 136, 255, 0.5)',
    color: '#9ac5ff',
  };
  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      padding: '6px 16px',
      borderRadius: '6px',
      border: '1px solid',
      fontFamily: theme.fonts.mono,
      ...variantStyle,
    }}>
      {kbd && (
        <kbd style={{
          display: 'inline-block',
          padding: '2px 8px',
          fontSize: '11px',
          fontFamily: theme.fonts.mono,
          fontWeight: 700,
          background: 'rgba(255, 255, 255, 0.06)',
          border: '1px solid rgba(255, 255, 255, 0.22)',
          borderRadius: '4px',
          letterSpacing: '1px',
          color: 'inherit',
        }}>{kbd}</kbd>
      )}
      <span style={{ fontSize: '11px', letterSpacing: '0.5px', lineHeight: 1.5 }}>{text}</span>
    </div>
  );
}

function MetaBadge({ label, color }: { label: string; color: string }) {
  return (
    <span style={{
      fontSize: '8px',
      fontWeight: 700,
      letterSpacing: '1.5px',
      padding: '2px 6px',
      borderRadius: '3px',
      border: `1px solid ${color}60`,
      background: 'rgba(255, 255, 255, 0.02)',
      color,
      fontFamily: theme.fonts.mono,
    }}>{label}</span>
  );
}

function KeywordBadge({ label, bg, border, color }: { label: string; bg: string; border: string; color: string }) {
  return (
    <span style={{
      background: bg,
      border: `1px solid ${border}`,
      color,
      fontSize: '8px',
      fontWeight: 700,
      letterSpacing: '1px',
      padding: '2px 6px',
      borderRadius: '3px',
      fontFamily: theme.fonts.mono,
    }}>{label}</span>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(8, 12, 20, 0.98)',
    overflow: 'auto',
    fontFamily: theme.fonts.mono,
  },
  inner: {
    maxWidth: '1100px',
    margin: '0 auto',
    padding: '32px 32px 64px',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '16px',
  },
  headerSpacer: { flex: 1 },
  backButton: {
    background: 'rgba(255, 255, 255, 0.03)',
    border: `1px solid ${theme.colors.border}`,
    color: theme.colors.textSecondary,
    fontFamily: theme.fonts.mono,
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '2px',
    padding: '8px 14px',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  title: {
    fontSize: '24px',
    fontWeight: 700,
    letterSpacing: '6px',
    color: theme.colors.textPrimary,
    margin: 0,
  },
  intro: {
    fontSize: '11px',
    color: theme.colors.textDim,
    lineHeight: 1.6,
    maxWidth: '640px',
    marginBottom: '32px',
  },
  section: {
    marginBottom: '40px',
    padding: '20px',
    background: 'rgba(12, 16, 24, 0.4)',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '6px',
  },
  sectionLabel: {
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '3px',
    color: theme.colors.textPrimary,
    marginBottom: '18px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  sectionMark: {
    fontSize: '16px',
    lineHeight: 1,
    color: theme.colors.accentBlue,
  },
  sectionBody: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '14px',
  },
  subsectionLabel: {
    fontSize: '9px',
    letterSpacing: '2px',
    color: theme.colors.textDim,
    fontWeight: 700,
    marginTop: '4px',
    lineHeight: 1.5,
  },

  // --- Card variant block ---
  cardVariantBlock: {
    padding: '16px',
    background: 'rgba(8, 12, 20, 0.5)',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '4px',
  },
  cardVariantLabel: {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '1px',
    color: theme.colors.accentBlue,
    marginBottom: '4px',
  },
  cardVariantNote: {
    fontSize: '10px',
    color: theme.colors.textDim,
    lineHeight: 1.5,
    marginBottom: '14px',
  },
  cardVariantShowcase: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    padding: '16px',
    background: 'rgba(0, 0, 0, 0.25)',
    borderRadius: '4px',
    alignItems: 'flex-start',
  },

  // --- Swatches ---
  swatch: {
    width: '110px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '4px',
  },
  swatchBlock: {
    width: '100%',
    height: '42px',
    borderRadius: '3px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
  },
  swatchName: {
    fontSize: '9px',
    color: theme.colors.textSecondary,
    fontWeight: 600,
    letterSpacing: '0.5px',
  },
  swatchHex: {
    fontSize: '8px',
    color: theme.colors.textDim,
    letterSpacing: '0.3px',
  },

  // --- Pile buttons (deck / discard) ---
  pile: {
    width: '70px',
    height: '80px',
    background: 'rgba(12, 16, 24, 0.95)',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '4px',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    cursor: 'pointer',
  },
  pileCount: {
    fontSize: '20px',
    fontWeight: 700,
    color: theme.colors.textPrimary,
    fontFamily: theme.fonts.mono,
  },
  pileLabel: {
    fontSize: '9px',
    letterSpacing: '1.5px',
    color: theme.colors.textDim,
    fontWeight: 700,
    fontFamily: theme.fonts.mono,
  },

  footer: {
    marginTop: '24px',
    fontSize: '10px',
    color: theme.colors.textDim,
    lineHeight: 1.6,
  },
  codePath: {
    display: 'inline-block',
    margin: '0 4px',
    padding: '1px 5px',
    background: 'rgba(255, 255, 255, 0.04)',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '3px',
    fontSize: '10px',
    color: theme.colors.textSecondary,
  },
};
